import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './assets/styles/bootstrap-overrides.css';
import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/AuthContext';
import { useProfile } from './contexts/ProfileContext';
import ProtectedRoute from './features/authentication/components/ProtectedRoute';
import AppLayout from './components/layout/AppLayout';
import DelayedLoading from './components/ui/DelayedLoading';

// Lazy-loaded components
const CustomerManagement = lazy(() => import('./features/customers/components/CustomerManagement'));
const CustomerUserProfileManagement = lazy(
  () => import('./features/customers/components/CustomerUserProfileManagement')
);
const SystemAdministratorsManagement = lazy(
  () => import('./features/administration/components/SystemAdministratorsManagement')
);
const SupportUsersManagement = lazy(
  () => import('./features/administration/components/SupportUsersManagement')
);
const Login = lazy(() => import('./features/authentication/components/Login'));
const AuthCallback = lazy(() => import('./features/authentication/components/AuthCallback'));
const FormPostHandler = lazy(() => import('./features/authentication/components/FormPostHandler'));
const ProfileSwitcher = lazy(() => import('./features/shared/components/ProfileSwitcher'));
const Unauthorized = lazy(() => import('./features/shared/components/Unauthorized'));
const NotFound = lazy(() => import('./features/shared/components/NotFound'));
const AccountActivation = lazy(
  () => import('./features/authentication/components/AccountActivation')
);
const ActivationComplete = lazy(
  () => import('./features/authentication/components/ActivationComplete')
);
const AccessDenied = lazy(() => import('./features/authentication/components/AccessDenied'));
const CustomerAdminDashboard = lazy(
  () => import('./features/customers/components/CustomerAdminDashboard')
);
const MonitoringDashboard = lazy(
  () => import('./features/monitoring/components/MonitoringDashboard')
);
const ErrorMonitor = lazy(() => import('./features/monitoring/components/ErrorMonitor'));
const ScheduleMonitor = lazy(() => import('./features/monitoring/components/ScheduleMonitor'));
const InstallerList = lazy(() => import('./features/installers/components/InstallerList'));
const InstallerCheck = lazy(() => import('./features/installerCheck/components/InstallerCheck'));
const TelemetryActivity = lazy(() => import('./features/telemetry/components/TelemetryActivity'));
const TelemetryHealthDashboard = lazy(
  () => import('./features/telemetry/components/TelemetryHealthDashboard')
);

// Preserve iswelcome query param through catch-all redirect to /login
function LoginRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const target = searchParams.get('iswelcome') === '1' ? '/login?iswelcome=1' : '/login';
  return <Navigate to={target} replace />;
}

// Component to handle role-based routing
const AppRoutes = () => {
  const { t } = useTranslation();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { isSystemAdmin, isCustomerAdmin, isCustomerUser, isSupportUser } = useProfile();

  // Show loading while auth is checking
  if (authLoading) {
    return <DelayedLoading fullHeight={true} text={t('common.loadingAuth')} />;
  }

  // Public routes when not authenticated
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/form-callback" element={<FormPostHandler />} />
        <Route path="/auth/access-denied" element={<AccessDenied />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="*" element={<LoginRedirect />} />
      </Routes>
    );
  }

  // Now we can safely use role booleans as profile is fully loaded
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/form-callback" element={<FormPostHandler />} />
      <Route path="/auth/access-denied" element={<AccessDenied />} />
      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Profile selection routes */}
      <Route path="/switch-profile" element={<ProfileSwitcher />} />

      {/* Activation routes */}
      <Route path="/auth/activate" element={<AccountActivation />} />
      <Route path="/auth/activation-complete" element={<ActivationComplete />} />

      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppLayout />}>
          {/* Home route redirects based on role */}
          <Route
            path="/"
            element={
              isSystemAdmin ? (
                <Navigate to="/customers" replace />
              ) : isSupportUser ? (
                <Navigate to="/customers" replace />
              ) : isCustomerAdmin ? (
                <CustomerAdminDashboard />
              ) : isCustomerUser ? (
                <div>Customer User Dashboard</div>
              ) : (
                <Navigate to="/switch-profile" replace />
              )
            }
          />

          {/* System Administrator routes */}
          {isSystemAdmin && (
            <>
              <Route path="/customers" element={<CustomerManagement />} />
              <Route path="/customers/:id/users" element={<CustomerUserProfileManagement />} />
              <Route path="/administration" element={<SystemAdministratorsManagement />} />
              <Route path="/support-users" element={<SupportUsersManagement />} />
              <Route path="/monitoring" element={<MonitoringDashboard />} />
              <Route path="/monitoring/errors" element={<ErrorMonitor />} />
              <Route path="/monitoring/schedules" element={<ScheduleMonitor />} />
              <Route path="/installers" element={<InstallerList />} />
              <Route path="/installer-check" element={<InstallerCheck />} />
            </>
          )}

          {/* Telemetry routes (System Administrator + Support User) */}
          {(isSystemAdmin || isSupportUser) && (
            <>
              <Route path="/telemetry/activity" element={<TelemetryActivity />} />
              <Route path="/telemetry/health" element={<TelemetryHealthDashboard />} />
            </>
          )}

          {/* Support User routes (view-only) */}
          {isSupportUser && (
            <>
              <Route path="/customers" element={<CustomerManagement />} />
              <Route path="/customers/:id/users" element={<CustomerUserProfileManagement />} />
              <Route path="/installers" element={<InstallerList />} />
              <Route path="/installer-check" element={<InstallerCheck />} />
            </>
          )}

          {/* Customer Admin routes */}
          {isCustomerAdmin && (
            <>
              <Route path="/organization/users" element={<CustomerUserProfileManagement />} />
              <Route path="/installers" element={<InstallerList />} />
              <Route path="/installer-check" element={<InstallerCheck />} />
            </>
          )}

          {/* Customer User routes */}
          {isCustomerUser && (
            <>
              <Route path="/" element={<div>Customer User Dashboard</div>} />
              <Route path="/installers" element={<InstallerList />} />
              <Route path="/installer-check" element={<InstallerCheck />} />
            </>
          )}
        </Route>
      </Route>

      {/* Fallback routes */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

function App() {
  const { loading } = useAuth();

  if (loading) {
    return <DelayedLoading fullHeight={true} />;
  }

  return (
    <Suspense fallback={<DelayedLoading fullHeight={true} />}>
      <AppRoutes />
    </Suspense>
  );
}

export default App;
